import createAuth0Client, {
  Auth0Client,
  Auth0ClientOptions,
  getIdTokenClaimsOptions,
  GetTokenSilentlyOptions,
  GetTokenWithPopupOptions,
  IdToken,
  LogoutOptions,
  PopupLoginOptions,
  RedirectLoginOptions,
  RedirectLoginResult,
} from '@auth0/auth0-spa-js';
import React, { createContext, useContext, useState } from 'react';

// Code below is boilerplate from Auth0 docs for React web
// https://manage.auth0.com/dashboard/us/dev-axz7fqik/applications/ek4lUT0y7jbpDZWEugn7N1lxwKyls8j8/quickstart
// Adapted to TS here:
// https://github.com/tommedema/startup-boilerplate/blob/master/packages/react-app/src/lib/auth0.tsx

export type Auth0RedirectState = {
  targetUrl?: string;
};

export type Auth0User = Exclude<IdToken, '__raw'>;

export type Auth0Context = {
  user?: Auth0User;
  isAuthenticated: boolean;
  isLoading: boolean;
  isPopupOpen: boolean;
  loginWithPopup(o?: PopupLoginOptions): Promise<void>;
  handleRedirectCallback(): Promise<RedirectLoginResult>;
  getIdTokenClaims(o?: getIdTokenClaimsOptions): Promise<IdToken>;
  loginWithRedirect(o?: RedirectLoginOptions): Promise<void>;
  getTokenSilently(o?: GetTokenSilentlyOptions): Promise<string | undefined>;
  getTokenWithPopup(o?: GetTokenWithPopupOptions): Promise<string | undefined>;
  logout(o?: LogoutOptions): void;
};

type Auth0ProviderOptions = {
  children: React.ReactElement;
  onRedirect(result: RedirectLoginResult): void;
};

export const Auth0Context = createContext<Auth0Context | undefined>(undefined);

export const useAuth0 = () => useContext(Auth0Context);

export const Auth0Provider = ({ children, onRedirect, ...initOptions }: Auth0ProviderOptions & Auth0ClientOptions) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<Auth0User>();
  const [auth0Client, setAuth0Client] = useState<Auth0Client>();
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  React.useEffect(() => {
    (async () => {
      const auth0FromHook = await createAuth0Client(initOptions);
      setAuth0Client(auth0FromHook);

      if (window.location.search.includes('code=')) {
        let appState: RedirectLoginResult = {};
        try {
          ({ appState } = await auth0FromHook.handleRedirectCallback());
        } finally {
          onRedirect(appState);
        }
      }

      const authed = await auth0FromHook.isAuthenticated();
      if (authed) {
        const userProfile = await auth0FromHook.getUser();
        setIsAuthenticated(true);
        setUser(userProfile);
      }

      setIsLoading(false);
    })();
  }, []);

  const loginWithPopup = async (options?: PopupLoginOptions) => {
    setIsPopupOpen(true);

    try {
      await auth0Client!.loginWithPopup(options);
    } catch (error) {
      console.error(error);
    } finally {
      setIsPopupOpen(false);
    }

    const userProfile = await auth0Client!.getUser();
    setUser(userProfile);
    setIsAuthenticated(true);
  };

  const handleRedirectCallback = async () => {
    setIsLoading(true);

    const result = await auth0Client!.handleRedirectCallback();
    const userProfile = await auth0Client!.getUser();

    setIsLoading(false);
    setIsAuthenticated(true);
    setUser(userProfile);

    return result;
  };

  const loginWithRedirect = (options?: RedirectLoginOptions) => auth0Client!.loginWithRedirect(options);

  const getTokenSilently = (options?: GetTokenSilentlyOptions) => auth0Client!.getTokenSilently(options);

  const logout = (options?: LogoutOptions) => auth0Client!.logout(options);

  const getIdTokenClaims = (options?: getIdTokenClaimsOptions) => auth0Client!.getIdTokenClaims(options);

  const getTokenWithPopup = (options?: GetTokenWithPopupOptions) => auth0Client!.getTokenWithPopup(options);

  return (
    <Auth0Context.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        isPopupOpen,
        loginWithPopup,
        loginWithRedirect,
        logout,
        getTokenSilently,
        handleRedirectCallback,
        getIdTokenClaims,
        getTokenWithPopup,
      }}>
      {children}
    </Auth0Context.Provider>
  );
};
